/**
 * Unified Slide Card Component
 * Smart adapter that renders the appropriate card based on slide format
 */

import React from 'react';
import { GsapSlideCard } from './GsapSlideCard';
import { UnifiedSlide } from '../../types/gsapSlide';

// Import existing components from SlideGallery
// We'll use the existing ExpandableSlideCard for HTML slides
interface Slide {
  id: string;
  number: string;
  title: string;
  path: string;
  content: string;
  source: 'plan' | 'html';
  hasHtml: boolean;
  justUpdated?: boolean;
}

interface SlideInfo {
  scene_id: string;
  title: string;
  layout_type: string;
  content_description?: string;
  facts_used?: string[];
  message?: string;
  component_mapping?: Record<string, string>;
  script_text?: string;
  sync_points?: string[];
  visual_instructions?: string;
  estimated_duration?: number;
  image_urls?: Array<{
    image_id: string;
    image_url: string;
  }>;
  start_time?: number;
  end_time?: number;
  generation_metadata?: {
    last_modified: string;
    timing_updated?: string;
  };
}

interface SlideGenerationStatus {
  status: 'pending' | 'generating' | 'completed' | 'error';
  progress?: number;
  jobId?: string;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
}

interface UnifiedSlideCardProps {
  // Support both UnifiedSlide (GSAP) and legacy Slide (HTML)
  slide: UnifiedSlide | Slide;
  slideInfo?: SlideInfo;
  extractSlideNumber?: (path: string) => string;
  extractTitle?: (content: string) => string;
  onSlideClick?: (slidePath: string) => void;
  onEditClick?: (slideId: string, sceneNumber: number) => void;
  formatDuration?: (seconds: number) => string;
  index?: number;
  conversationId?: string;
  generationStatus?: SlideGenerationStatus;

  // Component references for HTML slides
  ExpandableSlideCard?: React.ComponentType<any>;
}

export const UnifiedSlideCard: React.FC<UnifiedSlideCardProps> = (props) => {
  const {
    slide,
    slideInfo,
    extractSlideNumber,
    extractTitle,
    onSlideClick,
    onEditClick,
    formatDuration,
    index,
    conversationId,
    generationStatus,
    ExpandableSlideCard,
  } = props;

  // Determine if this is a GSAP slide with multiple fallback checks
  const isGsapSlide = (
    ('format' in slide && slide.format === 'gsap') ||  // Primary check: format field
    ('gsapData' in slide && !!(slide as any).gsapData) ||  // Fallback 1: has gsapData
    (slide.path?.endsWith('.json'))  // Fallback 2: .json file extension
  );

  // Debug logging
  console.log('🎴 UnifiedSlideCard - Rendering slide:', {
    slideId: slide.id,
    slideNumber: (slide as any).number,
    hasFormat: 'format' in slide,
    format: (slide as any).format,
    hasSource: 'source' in slide,
    source: (slide as any).source,
    hasContent: !!(slide as any).content,
    hasGsapData: !!(slide as any).gsapData,
    isGsapSlide,
    willRenderGsapCard: isGsapSlide,
    path: slide.path
  });

  if (isGsapSlide) {
    // Render GSAP slide card
    const gsapSlide = slide as UnifiedSlide;

    return (
      <GsapSlideCard
        slide={gsapSlide}
        onSlideClick={onSlideClick}
        onEditClick={onEditClick}
        conversationId={conversationId}
        generationStatus={generationStatus}
        index={index}
      />
    );
  }

  // Render HTML slide card using the provided component
  if (ExpandableSlideCard) {
    const htmlSlide = slide as Slide;

    return (
      <ExpandableSlideCard
        slide={htmlSlide}
        slideInfo={slideInfo}
        extractSlideNumber={extractSlideNumber}
        extractTitle={extractTitle}
        onSlideClick={onSlideClick}
        formatDuration={formatDuration}
        index={index}
      />
    );
  }

  // Fallback: shouldn't happen, but return null gracefully
  console.warn('UnifiedSlideCard: No appropriate component found for slide', slide);
  return null;
};

/**
 * Helper function to convert legacy Slide to UnifiedSlide format
 */
export function convertLegacySlideToUnified(
  legacySlide: Slide,
  slideInfo?: SlideInfo
): UnifiedSlide {
  return {
    id: legacySlide.id,
    number: legacySlide.number,
    title: legacySlide.title,
    path: legacySlide.path,
    format: 'html',
    content: legacySlide.content,
    duration: slideInfo?.estimated_duration ||
              (slideInfo?.end_time && slideInfo?.start_time
                ? slideInfo.end_time - slideInfo.start_time
                : undefined),
    layoutType: slideInfo?.layout_type,
    metadata: slideInfo ? {
      scene_id: slideInfo.scene_id,
      script_text: slideInfo.script_text,
      content_description: slideInfo.content_description,
      visual_instructions: slideInfo.visual_instructions,
      estimated_duration: slideInfo.estimated_duration,
      start_time: slideInfo.start_time,
      end_time: slideInfo.end_time,
    } : undefined,
  };
}

/**
 * Helper function to check if a slide is GSAP format
 */
export function isUnifiedGsapSlide(slide: UnifiedSlide | Slide): slide is UnifiedSlide {
  return 'format' in slide && slide.format === 'gsap';
}
